import React, { useCallback } from 'react';

import { Link, graphql, useStaticQuery } from 'gatsby';

import { useMixpanel } from '../../functions/Mixpanel';
import { Section } from '../Section';
import { Button } from '../web';
import { ResearchCard } from '../web/cards/researchCard';

type QueryEdgeData = {
  node: {
    title: string;
    type: string;
    subtitle: {
      subtitle: string;
      childMarkdownRemark: {
        html: string;
      };
    };
    sortOrder: number;
    link: string;
  };
};

type QueryData = {
  allContentfulResearchOnHomepage: {
    edges: QueryEdgeData[];
  };
};

const ResearchAndPub = () => {
  const { mixpanel } = useMixpanel();

  const data: QueryData = useStaticQuery(graphql`
    {
      allContentfulResearchOnHomepage {
        edges {
          node {
            sortOrder
            title
            type
            subtitle {
              subtitle
            }
            link
          }
        }
      }
    }
  `);

  const trackReadMore = useCallback(
    ({ type, title }: { type: string; title: string }) => {
      mixpanel.track('Read publication', {
        'Publication Type': type,
        'Publication Title': title,
      });
    },
    [mixpanel],
  );

  const trackViewMore = useCallback(() => {
    mixpanel.track('View More Research');
  }, [mixpanel]);

  const { edges } = data.allContentfulResearchOnHomepage;

  return (
    <Section
      bgClassName={classNames.background}
      className={classNames.container}
    >
      <h4 className={classNames.eyebrow}>Scientific Publications</h4>
      <h1 className={classNames.title}>Supported by peer-reviewed research</h1>
      <div className={classNames.grid}>
        {edges
          .filter((e) => e.node.sortOrder)
          .sort((a, b) => a.node.sortOrder - b.node.sortOrder)
          .map((e, i) => (
            <div key={i} className={i > 1 ? 'tw-hidden xl:tw-flex' : 'tw-flex'}>
              <ResearchCard
                eyebrow={e.node.type}
                title={e.node.title}
                subtitle={e.node.subtitle.subtitle}
                readMore={e.node.link}
                onReadMore={() =>
                  trackReadMore({
                    title: e.node.subtitle.subtitle,
                    type: e.node.type,
                  })
                }
              />
            </div>
          ))}
      </div>
      <Link to="/research">
        <Button
          className={classNames.button}
          preset="fabNoBackground"
          text="View more research"
          onClick={trackViewMore}
        />
      </Link>
    </Section>
  );
};

const classNames = {
  background: 'tw-bg-algae-100',
  container: [
    'tw-items-center',
    'md:tw-items-start',
    'tw-text-center',
    'md:tw-text-start',
  ].join(' '),
  eyebrow: ['tw-subtitle', 'tw-mb-[16px]', 'md:tw-mb-[24px]'].join(' '),
  title: ['tw-display3'].join(' '),
  grid: [
    'tw-grid',
    'tw-grid-rows-2',
    'tw-overflow-clip',
    'xl:tw-grid-cols-2',
    'tw-gap-[24px]',
    'tw-my-[48px]',
  ].join(' '),
  button: [
    'tw-max-w-[334px] tw-bg-algae-700',
    'hover:tw-bg-algae-200',
    'hover:tw-text-black',
  ].join(' '),
};

export { ResearchAndPub };
